///<reference path="../../../../typings/index.d.ts"/>

module Meevo.Components.Charts {


    const margin = {top: 20, right: 20, bottom: 30, left: 40};
    const CHART_WIDTH: number = 600;
    const CHART_HEIGHT: number = 400;

    type axisFormatter = (value: any) => string

    export interface ILineItem {
        x: number | Date;
        y: number | Date;
        dotColor: string;
    }

    export interface ILineChartDataRow {
        values: ILineItem[];
        key: string;
        color: string;
        strokeWidth?: number;
        classed?: string;
    }

    export interface ILineChartBuilderOptions {
        width?: number;
        height?: number;
        margin?: typeof margin;
        innerWidth?: number;
        innerHeight?: number;
        axisXFormatter?: axisFormatter;
        axisYFormatter?: axisFormatter;
    }

    const DEFAULT_OPTIONS: ILineChartBuilderOptions = {
        width: CHART_WIDTH,
        height: CHART_HEIGHT,
        innerWidth: CHART_WIDTH - margin.left - margin.right,
        innerHeight: CHART_HEIGHT - margin.top - margin.bottom,
        margin,
        axisXFormatter: (value) => value.toString(),
        axisYFormatter: (value) => value.toString(),
    };

    export class LineChartBuilder {

        protected chartSVG: d3.Selection<ILineChartDataRow[]>;
        protected xScale?: d3.time.Scale<any, any>;
        protected yScale?: d3.scale.Linear<any, any>;

        constructor(private chartPlace: HTMLElement, protected data: ILineChartDataRow[], protected options: ILineChartBuilderOptions = DEFAULT_OPTIONS) {
            this.options = _.extend({}, DEFAULT_OPTIONS, this.options);

            this.options.innerWidth = this.options.width - margin.left - margin.right;
            this.options.innerHeight = this.options.height - margin.top - margin.bottom;
        }

        protected getXMin(): number | Date {
            if (typeof this.data[0].values[0].x === 'number') {
                const min: number = Math.min(...this.data
                    .reduce((all, current) => [...all, ...current.values.map((value) => value.x)], []) as number[]) as number;

                return min - 10
            } else {
                const minDate: Date = this.data
                    .reduce((all, current) => [...all, ...current.values.map((value) => value.x)], [])
                    .reduce((minDate: Date, currentDate: Date) => {
                        return !minDate || minDate > currentDate ? currentDate : minDate;
                    }) as Date;

                // TODO: move marings to the this.options
                return moment(minDate).add('days', -15).toDate();
            }
        }

        protected getXMax(): number | Date {
            if (typeof this.data[0].values[0].x === 'number') {
                const max: number = Math.max(...this.data
                    .reduce((all, current) => [...all, ...current.values.map((value) => value.x)], []) as number[]);

                return max + 10;
            } else {
                const maxDate: Date = this.data
                    .reduce((all, current) => [...all, ...current.values.map((value) => value.x)], [])
                    .reduce((maxDate: Date, currentDate: Date) => !maxDate || maxDate < currentDate ? currentDate : maxDate);

                // TODO: move marings to the this.options
                return moment(maxDate).add('days', 15).toDate();
            }
        }

        protected getYMin(): number {
            return Math.min(...this.data
                .reduce((all, current) => [...all, ...current.values.map((value) => value.y)], []) as number[]) - 10;
        }

        protected getYMax(): number {
            return Math.max(...this.data
                .reduce((all, current) => [...all, ...current.values.map((value) => value.y)], []) as number[]) + 10;
        }

        buildBlock() {
            while (this.chartPlace.hasChildNodes()) {
                this.chartPlace.removeChild(this.chartPlace.lastChild);
            }

            this.chartSVG = d3.select(this.chartPlace)
                .append('svg')
                .datum(this.data)
                .attr('width', this.options.width)
                .attr('height', this.options.height)
                .append('g')
                .attr('transform', 'translate(' + this.options.margin.left + ',' + this.options.margin.top + ')');
        }

        buildXTimeScale() {
            this.xScale = d3.time.scale()
                .domain([this.getXMin() as Date, this.getXMax() as Date])
                .range([0, this.options.innerWidth]);
        }

        buildYLinearScale() {
            this.yScale = d3.scale.linear()
                .domain([this.getYMin(), this.getYMax()])
                .range([this.options.innerHeight, 0]);
        }

        buildAxises() {
            const xAxis = d3.svg.axis()
                .orient('bottom')
                .scale(this.xScale)
                .ticks(Math.max(...this.data.map((arr) => arr.values.length)))
                .tickFormat(this.options.axisXFormatter)

            this.chartSVG.append('g')
                .attr('class', 'axis axis--x')
                .attr('transform', 'translate(0,' + this.options.innerHeight + ')')
                .call(xAxis);


            const yAxis = d3.svg.axis()
                .scale(this.yScale)
                .orient('left')
                .innerTickSize(-this.options.innerWidth)
                .outerTickSize(1)
                .tickFormat(this.options.axisYFormatter)
                .tickPadding(15);

            this.chartSVG.append('g')
                .attr('class', 'axis axis--y grid')
                .call(yAxis);
        }

        buildLines() {
            const linesArea = this.chartSVG
                .append('g')
                .attr('class', 'lines-area');

            const line = d3.svg.line()
                .x((d: any) => this.xScale(d.x))
                .y((d: any) => this.yScale(d.y));

            for (const row of this.data) {
                // Doing lines

                const lines = linesArea
                    .append('g')
                    .attr('class', 'lines');

                lines
                    .append('path')
                    .attr('class', 'line')
                    .attr('d', line(row.values as any))
                    .style('stroke-width', 3)
                    .style('fill', 'none')
                    .attr('stroke', row.color)
                    .attr('data-legend', () => row.key);

                lines.selectAll('.dot')
                    .data(row.values)
                    .enter()
                    .append('circle')
                    .attr('class', 'dot')
                    .attr('stroke-width', '0')
                    .attr('fill', (d) => d.dotColor)
                    .attr('cx', line.x() as any)
                    .attr('cy', line.y() as any)
                    .attr('r', 3.5);

                const label = lines.selectAll('.label')
                    .data(row.values)
                    .enter()
                    .append('g')
                    .attr('class', 'label')
                    .attr('transform', (d, i) => 'translate(' + this.xScale(d.x as Date) + ',' + this.yScale(d.y as number) + ')');

                label.append('text')
                    .attr('dy', '-10px')
                    .attr('text-anchor', 'middle')
                    .attr('fill', (d) => d.dotColor)
                    .text((d) => d.y as number);
            }
        }

        buildLegend() {
            const legendItemMargin = {left: 5, right: 5};
            const legendSVG = d3.select(this.chartPlace)
                .append('svg')
                .datum(this.data)
                .attr('height', 30)
                .attr('width', 160) // TODO: make it dynamic
                .style('margin', '0 auto')
                .append('g');

            const legend = legendSVG
                .append('g')
                .attr('class', 'legend');

            for (const row of this.data) {
                const bBox: { x: number; y: number; width: number; height: number; } = (legend.node() as any).getBBox();
                const marginRight: number = bBox.x + bBox.width + 10;

                const legendItem = legend
                    .selectAll('legend-item')
                    .data([row])
                    .enter()
                    .append('g')
                    .attr('class', 'legend-item');

                const circleStartPoint: number = marginRight + legendItemMargin.left + legendItemMargin.right;
                const circleEndPoint: number = circleStartPoint + 7;
                legendItem
                    .append('circle')
                    .attr('cx', circleStartPoint)
                    .attr('cy', 5)
                    .attr('r', 3.5)
                    .style('fill', row.color);

                legendItem
                    .append('text')
                    .text((d) => d.key)
                    .attr('x', circleEndPoint + legendItemMargin.left)
                    .attr('y', 10);
            }
        }
    }
}
